import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Copy, Send, Trash2, ShieldCheck, KeyRound, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const ROLES = [
  { key: "admin",      label: "Administrator" },
  { key: "dispatcher", label: "Dispatcher" },
  { key: "customer",   label: "Customer (portal)" },
];

const roleLabel = (r) => ROLES.find((x) => x.key === r)?.label || r;

const rolePillClass = (r) => ({
  admin:      "bg-rose-100 text-rose-800 border-rose-300",
  dispatcher: "bg-indigo-100 text-indigo-800 border-indigo-300",
  customer:   "bg-emerald-100 text-emerald-800 border-emerald-300",
}[r] || "bg-zinc-100 text-zinc-700 border-zinc-300");

// ---------------------------------------------------------------------------
export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [credShown, setCredShown] = useState(null); // {email, password, email_status, email_error, role, name}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resend = async (u) => {
    if (!window.confirm(`Reset password for ${u.email} and send a fresh one by email?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/resend-credentials`);
      setCredShown({
        email: u.email,
        password: data.temp_password,
        role: u.role,
        name: u.name,
        email_status: data.email_status,
        email_error: data.email_error,
      });
      toast.success("Password reset — credentials shown.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="mono-label">Admin · dispatchers · customer portal accounts</p>
          <p className="text-sm text-zinc-600 mt-1">Create logins and send credentials by email. Only admins can access this page.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="sharp h-10 border-zinc-300" onClick={load} data-testid="users-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => setOpenNew(true)} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="users-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New user
          </Button>
        </div>
      </div>

      <div className="border border-zinc-950 bg-white overflow-hidden">
        <table className="apex-grid">
          <thead>
            <tr>
              {["Email", "Name", "Role", "Company", "Created", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2 mono-label !text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center mono-label">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center mono-label">No users yet</td></tr>
            ) : rows.map((u) => (
              <tr key={u.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${rolePillClass(u.role)}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-700">{u.company || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resend(u)}
                      className="sharp h-8 border-zinc-300"
                      title="Reset password + email new credentials"
                      data-testid={`resend-${u.email}`}
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset & Send
                    </Button>
                    <button
                      onClick={() => remove(u)}
                      className="h-8 w-8 flex items-center justify-center border border-zinc-300 text-rose-700 hover:bg-rose-50"
                      title="Delete user"
                      data-testid={`delete-${u.email}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewUserDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onCreated={(cred) => { setCredShown(cred); load(); }}
      />

      <CredentialsDialog
        cred={credShown}
        onClose={() => setCredShown(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    email: "", name: "", role: "dispatcher", company: "", phone: "",
    password: "", send_email: true,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ email: "", name: "", role: "dispatcher", company: "", phone: "", password: "", send_email: true });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users", {
        ...form,
        email: form.email.trim().toLowerCase(),
        password: form.password || undefined,
      });
      const status = data.email_status;
      if (status === "sent") toast.success("User created & credentials emailed.");
      else if (status === "not_configured") toast.info("User created. SMTP not configured — copy the password below.");
      else if (status === "failed") toast.warning("User created but email failed. Copy the password below.");
      onOpenChange(false);
      onCreated({
        email: data.user.email,
        password: data.temp_password,
        role: data.user.role,
        name: data.user.name,
        email_status: data.email_status,
        email_error: data.email_error,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-lg border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">New user</DialogTitle>
          <DialogDescription className="text-sm text-zinc-600">
            Create a login for a new admin, dispatcher, or customer-portal user.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="mono-label">Email</label>
              <Input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="sharp mt-1 apex-input" placeholder="dispatcher@apexscs.ca"
                data-testid="new-user-email" />
            </div>
            <div>
              <label className="mono-label">Full name</label>
              <Input required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="sharp mt-1 apex-input" data-testid="new-user-name" />
            </div>
            <div>
              <label className="mono-label">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="sharp mt-1 h-10" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mono-label">Company (optional)</label>
              <Input value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="sharp mt-1 apex-input" />
            </div>
            <div>
              <label className="mono-label">Phone (optional)</label>
              <Input value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="sharp mt-1 apex-input" />
            </div>
            <div className="col-span-2">
              <label className="mono-label">Password (leave blank to auto-generate)</label>
              <Input value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="sharp mt-1 apex-input font-mono"
                placeholder="Auto-generated 12-character password"
                data-testid="new-user-password" />
            </div>
          </div>

          <div className="flex items-center gap-2 border-t border-zinc-200 pt-3">
            <Checkbox id="send-email" checked={form.send_email}
              onCheckedChange={(v) => setForm({ ...form, send_email: !!v })}
              data-testid="new-user-send-email" />
            <label htmlFor="send-email" className="text-sm cursor-pointer">
              Email login credentials to the new user
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="sharp border-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="new-user-submit">
              {busy ? "Creating..." : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function CredentialsDialog({ cred, onClose }) {
  const open = !!cred;
  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };
  if (!cred) return null;

  const statusMap = {
    sent:            { color: "bg-emerald-600", label: "EMAILED", note: "Credentials emailed to the user." },
    not_configured:  { color: "bg-amber-500",   label: "EMAIL PENDING", note: "SMTP is not configured yet. Copy the password below and share it manually — once SMTP is set up, future accounts will be emailed automatically." },
    failed:          { color: "bg-rose-600",    label: "EMAIL FAILED", note: `Delivery failed — copy the password below and share it manually.${cred.email_error ? ` (${cred.email_error})` : ""}` },
  };
  const s = statusMap[cred.email_status] || statusMap.not_configured;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sharp max-w-lg border-zinc-950" data-testid="credentials-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            Credentials ready
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-600">
            Save these credentials — the password won&apos;t be shown again.
          </DialogDescription>
        </DialogHeader>

        <div className={`p-3 text-white ${s.color}`}>
          <div className="flex items-center gap-2 font-semibold tracking-wide text-xs uppercase">
            <Mail className="h-4 w-4" /> {s.label}
          </div>
          <p className="text-xs mt-1 opacity-90">{s.note}</p>
        </div>

        <div className="border border-zinc-950 divide-y divide-zinc-200">
          <CredentialRow label="Email"    value={cred.email}    onCopy={copy} testId="cred-email" />
          <CredentialRow label="Password" value={cred.password} onCopy={copy} testId="cred-password" mono />
          <CredentialRow label="Role"     value={roleLabel(cred.role)} onCopy={copy} testId="cred-role" />
        </div>

        <div className="text-[11px] text-zinc-600 font-mono">
          Advise the user to change their password on first login.
        </div>

        <DialogFooter>
          <Button
            onClick={() => copy(`Email: ${cred.email}\nPassword: ${cred.password}\nRole: ${roleLabel(cred.role)}`)}
            variant="outline"
            className="sharp border-zinc-300"
            data-testid="cred-copy-all"
          >
            <Copy className="h-4 w-4 mr-1.5" /> Copy all
          </Button>
          <Button onClick={onClose} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="cred-done">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredentialRow({ label, value, onCopy, mono, testId }) {
  return (
    <div className="flex items-stretch">
      <div className="w-28 shrink-0 bg-zinc-50 px-3 py-2 mono-label border-r border-zinc-200 flex items-center">{label}</div>
      <div className={`flex-1 px-3 py-2 flex items-center justify-between ${mono ? "font-mono font-semibold text-[#0056B3]" : ""}`}>
        <span className="truncate" data-testid={testId}>{value}</span>
        <button
          type="button"
          onClick={() => onCopy(value)}
          className="ml-2 p-1 border border-zinc-300 hover:bg-zinc-100"
          title="Copy"
          data-testid={`${testId}-copy`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
